import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { IRSQuestion, IRSAnswerGroup } from '../../../types/irsForms';
import { generateUUID32Hex } from '../../../utils/uuid';
import IRSFormQuestion from './IRSFormQuestion';
import { goClient } from '../../../utils/httpClientTs';

interface IRSFormRepeatingGroupProps {
  parentQuestion: IRSQuestion;
  formId: string;
  editable: boolean;
  onAnswerChange: (
    questionId: string,
    value: any,
    answerType: string,
    answerGroupId?: string
  ) => void;
  onOtherItemsUpdate?: () => void;
  onSavingChange?: (saving: boolean) => void;
  saveStatus?: Record<string, 'idle' | 'saving' | 'saved' | 'error'>;
  saveErrors?: Record<string, string>;
  onRetrySave?: (questionId: string) => void;
  answers: Record<string, any>;
  childQuestionsMap: Record<string, IRSQuestion[]>;
}

const IRSFormRepeatingGroup: React.FC<IRSFormRepeatingGroupProps> = ({
  parentQuestion,
  formId,
  editable,
  onAnswerChange,
  onOtherItemsUpdate,
  onSavingChange,
  saveStatus = {},
  saveErrors = {},
  onRetrySave,
  answers,
  childQuestionsMap,
}) => {
  // Initialize local groups from parent question's answer_groups
  // Also sync with parent when it updates (e.g., after refetch)
  const [localGroups, setLocalGroups] = useState<IRSAnswerGroup[]>(
    parentQuestion.answer_groups || []
  );

  // Sync local groups when parent question updates
  // Also filter out groups that have no answers with values (empty groups)
  React.useEffect(() => {
    if (
      parentQuestion.answer_groups &&
      parentQuestion.answer_groups.length > 0
    ) {
      // Filter out groups that have no answers with actual values
      // (groups where all answers are null/empty)
      const groupsWithAnswers = parentQuestion.answer_groups.filter((group) => {
        // A group is valid if it has at least one answer with a non-null value
        return (
          group.answers &&
          group.answers.length > 0 &&
          group.answers.some((answer) => {
            // Check if answer has any non-null, non-empty value
            const hasValue =
              (answer.text_answer !== null &&
                answer.text_answer !== undefined &&
                answer.text_answer !== '') ||
              (answer.decimal_answer !== null &&
                answer.decimal_answer !== undefined) ||
              (answer.integer_answer !== null &&
                answer.integer_answer !== undefined) ||
              (answer.boolean_answer !== null &&
                answer.boolean_answer !== undefined) ||
              (answer.date_answer !== null && answer.date_answer !== undefined);
            return hasValue;
          })
        );
      });
      setLocalGroups(groupsWithAnswers);
    } else {
      // If no answer_groups exist yet, keep any locally added group (e.g. auto-created when parent is true)
      setLocalGroups((prev) => (prev.length > 0 ? prev : []));
    }
  }, [parentQuestion.answer_groups]);
  const [deletingGroups, setDeletingGroups] = useState<Set<string>>(new Set());

  // Get all child questions (including nested) for this parent
  const getAllChildQuestions = useCallback(
    (parentId: string): IRSQuestion[] => {
      const directChildren = childQuestionsMap[parentId] || [];
      const allChildren: IRSQuestion[] = [...directChildren];

      // Recursively get nested children
      directChildren.forEach((child) => {
        const nested = getAllChildQuestions(child.question_id);
        allChildren.push(...nested);
      });

      return allChildren;
    },
    [childQuestionsMap]
  );

  // Get child questions from childQuestionsMap (for questions not in answer groups)
  const childQuestionsFromMap = getAllChildQuestions(
    parentQuestion.question_id
  );

  // Get child questions from existing answer groups (if any)
  // Extract unique questions from all answer groups
  const childQuestionsFromGroups = React.useMemo(() => {
    const questionMap = new Map<string, IRSQuestion>();
    if (parentQuestion.answer_groups) {
      parentQuestion.answer_groups.forEach((group) => {
        group.answers.forEach((answer) => {
          // Use answer as question (it has all question metadata)
          if (answer.question_id && !questionMap.has(answer.question_id)) {
            questionMap.set(answer.question_id, answer as IRSQuestion);
          }
        });
      });
    }
    return Array.from(questionMap.values());
  }, [parentQuestion.answer_groups]);

  // Combine both sources, prioritizing childQuestionsMap (more complete)
  // But use questions from groups if map is empty (fallback)
  const allChildQuestions = React.useMemo(() => {
    if (childQuestionsFromMap.length > 0) {
      return childQuestionsFromMap;
    }
    // Fallback: if childQuestionsMap is empty for some reason, use questions from answer groups
    return childQuestionsFromGroups;
  }, [childQuestionsFromMap, childQuestionsFromGroups]);
  const parentAnswer = answers[parentQuestion.question_id];

  // When parent is true and there are no groups, create one by default so the user sees the form to fill out
  React.useEffect(() => {
    if (
      parentAnswer === true &&
      localGroups.length === 0 &&
      allChildQuestions.length > 0 &&
      editable
    ) {
      const newGroupId = generateUUID32Hex();
      const newGroup: IRSAnswerGroup = {
        answer_group_id: newGroupId,
        answers: allChildQuestions.map((child) => ({
          ...child,
          answer_group_id: newGroupId,
        })),
      };
      setLocalGroups((prev) => [...prev, newGroup]);
    }
  }, [parentAnswer, localGroups.length, allChildQuestions, editable]);

  // Handle parent question answer change
  const handleParentAnswerChange = (value: any) => {
    onAnswerChange(
      parentQuestion.question_id,
      value,
      parentQuestion.answer_type
    );
  };

  // Add a new answer group
  const handleAddGroup = useCallback(() => {
    const newGroupId = generateUUID32Hex();

    // Ensure we have child questions to add
    if (allChildQuestions.length === 0) {
      console.warn('No child questions available to add to new group', {
        parentQuestionId: parentQuestion.question_id,
        childQuestionsMap,
        allChildQuestions,
      });
      return;
    }

    console.log('Creating new group with child questions:', {
      newGroupId,
      childQuestionCount: allChildQuestions.length,
      childQuestions: allChildQuestions.map((q) => ({
        id: q.question_id,
        text: q.question_text,
      })),
    });

    const newGroup: IRSAnswerGroup = {
      answer_group_id: newGroupId,
      // Create answer objects from child questions
      // Each answer will have the question metadata plus answer_group_id
      answers: allChildQuestions.map((child) => ({
        ...child,
        answer_group_id: newGroupId,
      })),
    };
    setLocalGroups([...localGroups, newGroup]);
  }, [
    localGroups,
    allChildQuestions,
    parentQuestion.question_id,
    childQuestionsMap,
  ]);

  // Delete an answer group
  const handleDeleteGroup = useCallback(
    async (groupId: string) => {
      if (
        !window.confirm(
          'Are you sure you want to delete this entire group? This action cannot be undone.'
        )
      ) {
        return;
      }

      setDeletingGroups((prev) => new Set(prev).add(groupId));

      try {
        // Delete the entire answer group in one request so all rows for this group_id
        // are removed (avoids splitting questions across funds when only group.answers was used)
        await goClient.delete(
          `/api/annual-reports/irs/${formId}/answer-groups/${groupId}`
        );

        // Remove group from local state immediately
        setLocalGroups((prev) =>
          prev.filter((g) => g.answer_group_id !== groupId)
        );

        if (onOtherItemsUpdate) {
          onOtherItemsUpdate();
        }
      } catch (error: any) {
        console.error('Failed to delete group:', error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to delete group. Please try again.';
        alert(errorMessage);
      } finally {
        setDeletingGroups((prev) => {
          const updated = new Set(prev);
          updated.delete(groupId);
          return updated;
        });
      }
    },
    [formId, onOtherItemsUpdate]
  );

  // Render a single answer group
  const renderAnswerGroup = (group: IRSAnswerGroup, index: number) => {
    const isDeleting = deletingGroups.has(group.answer_group_id);

    return (
      <Card
        key={group.answer_group_id}
        sx={{
          mb: 2,
          opacity: isDeleting ? 0.5 : 1,
          position: 'relative',
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6" component="h3">
              Fund {index + 1}
            </Typography>
            {editable && (
              <IconButton
                onClick={() => handleDeleteGroup(group.answer_group_id)}
                disabled={isDeleting}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          {/* Render all child questions in this group */}
          {group.answers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No questions available for this fund.
            </Typography>
          ) : (
            // Sort answers by display_order before rendering
            [...group.answers]
              .sort((a, b) => {
                const orderA = a.display_order || 0;
                const orderB = b.display_order || 0;
                return orderA - orderB;
              })
              .map((answer) => {
                // The answer object contains question metadata (question_id, question_text, etc.)
                // For new groups, the answer IS the question object
                // For existing groups from the API, the answer also contains question metadata
                // So we can use the answer object directly as the question
                // But first try to get the full question from allChildQuestions if available
                // (it might have more complete metadata)
                let childQuestion = allChildQuestions.find(
                  (q) => q.question_id === answer.question_id
                );

                // If not found in allChildQuestions, use the answer object itself
                // (it has all the question metadata we need)
                if (!childQuestion && answer && answer.question_id) {
                  childQuestion = answer as IRSQuestion;
                }

                // If still no question, skip rendering this answer
                if (!childQuestion) {
                  console.warn(`Could not find question for answer:`, answer);
                  return null;
                }

                // Get the answer value for this question in this group
                const answerKey = `${answer.question_id}_${group.answer_group_id}`;
                const localAnswer = answers[answerKey];

                // Determine the actual answer value
                // Priority: 1) local answer (from state), 2) answer object fields, 3) null
                let answerValue: any = null;
                if (localAnswer !== undefined) {
                  // Local answer is a simple value (string/number/boolean) or null
                  answerValue = localAnswer;
                } else if (answer && typeof answer === 'object') {
                  // Check if answer has answer fields (it's an actual answer object)
                  if (
                    'text_answer' in answer ||
                    'decimal_answer' in answer ||
                    'integer_answer' in answer ||
                    'boolean_answer' in answer ||
                    'date_answer' in answer
                  ) {
                    answerValue =
                      answer.text_answer ??
                      answer.decimal_answer ??
                      answer.integer_answer ??
                      answer.boolean_answer ??
                      answer.date_answer ??
                      null;
                  }
                  // Otherwise, answer is just a question template, so answerValue stays null
                }

                // For nested children, find the parent answer within this group
                // Build a map of answers in this group for quick lookup
                const groupAnswersMap = new Map<string, any>();
                group.answers.forEach((a) => {
                  const aKey = `${a.question_id}_${group.answer_group_id}`;
                  const aLocalAnswer = answers[aKey];
                  let aValue: any = null;
                  if (aLocalAnswer !== undefined) {
                    aValue = aLocalAnswer;
                  } else if (a && typeof a === 'object') {
                    if (
                      'text_answer' in a ||
                      'decimal_answer' in a ||
                      'integer_answer' in a ||
                      'boolean_answer' in a ||
                      'date_answer' in a
                    ) {
                      aValue =
                        a.text_answer ??
                        a.decimal_answer ??
                        a.integer_answer ??
                        a.boolean_answer ??
                        a.date_answer ??
                        null;
                    }
                  }
                  groupAnswersMap.set(a.question_id, aValue);
                });

                // Determine the correct parentAnswer for this question
                // If it has a parent_question_id, use the answer from that parent question in this group
                // Otherwise, use the parent question's answer (the boolean for question 11)
                let questionParentAnswer = parentAnswer;
                if (childQuestion.parent_question_id) {
                  const parentQuestionAnswer = groupAnswersMap.get(
                    childQuestion.parent_question_id
                  );
                  if (parentQuestionAnswer !== undefined) {
                    questionParentAnswer = parentQuestionAnswer;
                  }
                }

                return (
                  <Box
                    key={`${answer.question_id}_${group.answer_group_id}`}
                    sx={{ mb: 2 }}
                  >
                    <IRSFormQuestion
                      question={childQuestion}
                      answer={answerValue}
                      onChange={(value) =>
                        onAnswerChange(
                          childQuestion.question_id,
                          value,
                          childQuestion.answer_type,
                          group.answer_group_id
                        )
                      }
                      disabled={!editable || isDeleting}
                      parentAnswer={questionParentAnswer}
                      formId={formId}
                      onOtherItemsUpdate={onOtherItemsUpdate}
                      onSavingChange={onSavingChange}
                      saveStatus={saveStatus[answerKey] || 'idle'}
                      saveError={saveErrors[answerKey]}
                      onRetrySave={() => onRetrySave?.(answerKey)}
                      answerGroupId={group.answer_group_id}
                    />
                  </Box>
                );
              })
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Render parent question */}
      <Box sx={{ mb: 2 }}>
        <IRSFormQuestion
          question={parentQuestion}
          answer={parentAnswer}
          onChange={handleParentAnswerChange}
          disabled={!editable}
          formId={formId}
          onOtherItemsUpdate={onOtherItemsUpdate}
          onSavingChange={onSavingChange}
          saveStatus={saveStatus[parentQuestion.question_id] || 'idle'}
          saveError={saveErrors[parentQuestion.question_id]}
          onRetrySave={() => onRetrySave?.(parentQuestion.question_id)}
        />
      </Box>

      {/* Render answer groups if parent answer is true */}
      {parentAnswer === true && (
        <Box sx={{ ml: 2, mt: 2 }}>
          {localGroups.map((group, index) => renderAnswerGroup(group, index))}

          {editable && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddGroup}
              sx={{ mt: 2 }}
            >
              Add Another
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default IRSFormRepeatingGroup;
